import React, { useMemo, useState } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import ApexChart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { usePortfolioSnapshotHistory } from '../hooks/usePortfolioSnapshotHistory';

// Simple daily portfolio-value trend line. Replaces the transaction-
// derived history charts on PortfolioPage when TRANSACTIONS_ENABLED
// is false. Data is sourced from GET /api/portfolio/snapshot-history
// (one row per user per day, written by balanceSnapshotJob).
const RANGES = [
  { key: '7d', days: 7 },
  { key: '30d', days: 30 },
  { key: '90d', days: 90 },
  { key: '1y', days: 365 },
];

export default function PortfolioSnapshotChart() {
  const { t } = useTranslation();
  const [rangeKey, setRangeKey] = useState('30d');
  const days = useMemo(
    () => RANGES.find((r) => r.key === rangeKey)?.days || 30,
    [rangeKey]
  );
  const { data, loading, error } = usePortfolioSnapshotHistory(days);

  const series = useMemo(
    () => [
      {
        name: t('portfolio.snapshotChartTitle', 'Portfolio value'),
        data: (data || []).map((row) => [
          new Date(row.date + 'T00:00:00Z').getTime(),
          Number(row.totalValueUsd) || 0,
        ]),
      },
    ],
    [data, t]
  );

  const options = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        background: '#fff',
        animations: { enabled: true },
        fontFamily: 'inherit',
      },
      dataLabels: { enabled: false },
      xaxis: {
        type: 'datetime',
        labels: {
          style: { fontSize: '10px', color: '#888' },
          datetimeUTC: false,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: false },
      },
      yaxis: {
        show: true,
        labels: {
          style: { fontSize: '10px', color: '#1976d2' },
          formatter: (val) =>
            '$' +
            (val
              ? Number(val).toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })
              : '0.00'),
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        forceNiceScale: true,
      },
      stroke: { width: 3, curve: 'straight', colors: ['#1976d2'] },
      colors: ['#1976d2'],
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.3,
          gradientToColors: ['#1976d2'],
          inverseColors: false,
          opacityFrom: 0.5,
          opacityTo: 0.1,
          stops: [0, 100],
        },
      },
      markers: { size: 0 },
      tooltip: {
        enabled: true,
        x: { format: 'dd MMM yyyy' },
        y: {
          formatter: (val) =>
            '$' +
            (val
              ? Number(val).toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })
              : '0.00'),
        },
        theme: 'light',
        marker: { show: false },
      },
      grid: {
        show: true,
        borderColor: '#e0e0e0',
        strokeDashArray: 4,
      },
      legend: { show: false },
    }),
    []
  );

  const hasData = (data || []).length > 0;

  return (
    <Box
      sx={{
        mb: 2,
        width: '100%',
        bgcolor: '#fff',
        borderRadius: 2,
        p: { xs: 0.5, sm: 2 },
        minHeight: 180,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2' }}>
          {t('portfolio.snapshotChartTitle', 'Portfolio value (daily)')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.2 }}>
          {RANGES.map((r) => (
            <Button
              key={r.key}
              onClick={() => setRangeKey(r.key)}
              variant="text"
              sx={{
                minWidth: 36,
                px: 0.8,
                py: 0.2,
                fontSize: '0.8rem',
                height: 28,
                textTransform: 'none',
                bgcolor: rangeKey === r.key ? '#e6f0fa' : 'transparent',
                color: rangeKey === r.key ? '#1976d2' : '#222',
              }}
            >
              {r.key}
            </Button>
          ))}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 160 }}>
          <CircularProgress size={24} />
        </Box>
      ) : error || !hasData ? (
        // We intentionally show the same friendly empty state for both
        // "request failed" and "no snapshots yet" — the job needs at
        // least one day to populate the first row, so a fresh environment
        // will look empty until the first run.
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 160 }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              'portfolio.snapshotEmpty',
              'No history yet — your portfolio value will start appearing here after the first daily snapshot.'
            )}
          </Typography>
        </Box>
      ) : (
        <ApexChart type="area" height={180} series={series} options={options} />
      )}
    </Box>
  );
}
